import { yupResolver } from "@hookform/resolvers/yup";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { useGoalsFulfillmentContext } from "Components/Analyses/GoalsFulfillment/GoalsFulfillmentContext";
import { DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type InvestmentsFormType, getInvestmentsFormSchema } from "Forms/Schemas/GoalsFulfillment/ProposedSolution/investmentsFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetAssetAllocationOverviewQueryKey,
  getListContractProposalsQueryKey,
  useCreateContractProposal,
  usePatchContractProposal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type ContractProposalBaseSolutionHorizonType,
  CreateContractProposalInvestmentBodyInvestmentToolType,
  PatchContractProposalInvestmentBodyInvestmentToolType,
} from "Infrastructure/ApiZfp/Schema";
import type { CreateContractProposalInvestmentBody, PatchContractProposalInvestmentBody } from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailInvestment } from "Infrastructure/ApiZfp/Schema/contractProposalDetailInvestment";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { CharacteristicsAccordion } from "../../CharacteristicsAccordion";
import { InvestmentsNsureEvaluationPanel } from "./InvestmentsNsureEvaluationPanel";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";

type Props = {
  readonly onBack: () => void;
  readonly onClose: () => void;
  readonly onDelete?: () => void;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly edit?: boolean;
  readonly proposedSolutionId?: string;
  readonly initialData?: ContractProposalDetailInvestment | null;
};

export const InvestmentsNsureDetails = ({ onBack, onClose, onDelete, horizonType, edit, proposedSolutionId, initialData }: Props) => {
  const { t } = useResource();
  const { selection, setSelection } = useContext(NsureSelectionContext);
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { assetAllocationOverview } = useGoalsFulfillmentContext();
  const queryClient = useQueryClient();
  const { mutate: createMutation } = useCreateContractProposal();
  const { mutate: updateMutation } = usePatchContractProposal();

  const minRegularAmount = (selection?.strategyInstruments?.length ?? 0) * 500;

  const methods = useForm<InvestmentsFormType>({
    resolver: yupResolver(
      getInvestmentsFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired, minRegularAmount || undefined),
    ),
    mode: "onChange",
    defaultValues:
      edit && initialData
        ? {
            investmentToolType: CreateContractProposalInvestmentBodyInvestmentToolType.investment,
            oneTimeDeposit: initialData.oneTimeAmount?.value ?? 0,
            regularAmount: initialData.regularAmount?.value ?? 0,
            horizon: initialData.currentSolutionHorizon ?? 0,
            appreciationRate: initialData.appreciationRate ?? selection?.expectedPerformance,
            entryFee: initialData.entryFee ?? 0,
            oneTimeFee: initialData.oneTimeFee?.value ?? 0,
            managementFee: initialData.managementFee ?? 0,
            exitFee: initialData.exitFee ?? 0,
            equities: initialData.assetAllocation?.equities ?? 0,
            bonds: initialData.assetAllocation?.bonds ?? 0,
            commodities: initialData.assetAllocation?.commodities ?? 0,
            realEstate: initialData.assetAllocation?.realEstate ?? 0,
            cash: initialData.assetAllocation?.cash ?? 0,
            other: initialData.assetAllocation?.others ?? 0,
            note: initialData.note ?? "",
          }
        : {
            appreciationRate: selection?.expectedPerformance ?? undefined,
            investmentToolType: CreateContractProposalInvestmentBodyInvestmentToolType.investment,
          },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = methods;

  const buildPortfolio = (sel: typeof selection) => ({
    name: sel?.name ?? undefined,
    esg: sel?.esg ?? undefined,
    srri: sel?.srri ?? undefined,
    sri: sel?.sri ?? undefined,
    expectedPerformance: sel?.expectedPerformance ?? undefined,
    strategyTemplateID: sel?.type === NsureSelectionItemType.Portfolio ? String(sel.id) : undefined,
    strategyInstruments: (sel?.strategyInstruments ?? []).map(({ id, participationInPercentage }) => ({
      instrumentID: id,
      participationInPercentage,
    })),
  });

  const handleBack = () => {
    setSelection(null);
    onBack();
  };

  const onSubmit = (data: InvestmentsFormType) => {
    if (!selection) return;

    if (edit && proposedSolutionId) {
      const payload: PatchContractProposalInvestmentBody = {
        investmentToolType: PatchContractProposalInvestmentBodyInvestmentToolType.investment,
        solutionHorizonType: initialData?.solutionHorizonType,
        currentSolutionHorizon: data.horizon ?? 0,
        appreciationRate: data.appreciationRate,
        managementFee: data.managementFee ?? 0,
        entryFee: data.entryFee ?? 0,
        exitFee: data.exitFee ?? 0,
        note: data.note ?? "",
        oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
        regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
        oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
        assetAllocation: {
          equities: data.equities ?? 0,
          bonds: data.bonds ?? 0,
          commodities: data.commodities ?? 0,
          realEstate: data.realEstate ?? 0,
          cash: data.cash ?? 0,
          others: data.other ?? 0,
        },
        portfolio: buildPortfolio(selection),
      };

      updateMutation(
        { analysisId, versionId, contractProposalGuid: proposedSolutionId, data: payload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            setSelection(null);
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
      return;
    }

    const payload: CreateContractProposalInvestmentBody = {
      investmentToolType: CreateContractProposalInvestmentBodyInvestmentToolType.investment,
      solutionHorizonType: horizonType,
      currentSolutionHorizon: data.horizon ?? 0,
      appreciationRate: data.appreciationRate,
      managementFee: data.managementFee ?? 0,
      entryFee: data.entryFee ?? 0,
      exitFee: data.exitFee ?? 0,
      note: data.note ?? "",
      oneTimeAmount: { value: data.oneTimeDeposit ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      regularAmount: { value: data.regularAmount ?? 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
      oneTimeFee: { value: data.oneTimeFee ?? 0, currencyCode: "CZK", frequency: AmountFrequency.one_time },
      assetAllocation: {
        equities: data.equities ?? 0,
        bonds: data.bonds ?? 0,
        commodities: data.commodities ?? 0,
        realEstate: data.realEstate ?? 0,
        cash: data.cash ?? 0,
        others: data.other ?? 0,
      },
      portfolio: buildPortfolio(selection),
    };

    createMutation(
      { analysisId, versionId, data: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
          setSelection(null);
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        },
      },
    );
  };

  if (!selection) {
    return <Alert variant="error" text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Details.LoadError)} />;
  }

  return (
    <FormProvider {...methods}>
      <>
        <DrawerContent>
          <Stack spacing={Spacing.S} px={Spacing.XXL}>
            {!edit && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<KeyboardArrowLeftIcon fontSize="small" sx={{ position: "relative", top: "-1px" }} />}
                onClick={handleBack}
                sx={{ alignSelf: "flex-start" }}
              >
                {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Details.BackToSelection)}
              </Button>
            )}

            <Flex sx={{ flexDirection: { xs: "column-reverse", lg: "row" } }} gap={Spacing.XL} pt={Spacing.L}>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                  display="block"
                  pb={Spacing.M}
                >
                  {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Details.Title)}
                </Typography>

                <Box border="1px solid" borderColor="divider" borderRadius={Radius.L} bgcolor="white" p={Spacing.XL}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={Spacing.XL}>
                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          name="oneTimeDeposit"
                          label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeDeposit)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              {t(Resources.Common.CZKSuffix)}
                            </Typography>
                          }
                          unmask="typed"
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                            },
                          ]}
                        />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          name="regularAmount"
                          label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                          labelEndAdornment={<StyledLabelAdornment />}
                          errors={errors}
                          unmask="typed"
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                            },
                          ]}
                          inputEndAdornment={
                            <Box sx={{ width: 100 }}>
                              <Typography variant="body2" color="secondary">
                                {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          name="horizon"
                          label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              {t(Resources.Common.Years)}
                            </Typography>
                          }
                          unmask="typed"
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 0,
                            },
                          ]}
                        />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          name="appreciationRate"
                          label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                          labelEndAdornment={<StyledLabelAdornment />}
                          errors={errors}
                          unmask="typed"
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                            },
                          ]}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              %
                            </Typography>
                          }
                        />
                      </Grid>

                      <Divider sx={{ width: "100%" }} />

                      <Stack spacing={Spacing.L} sx={{ width: "100%" }}>
                        <Typography variant="body1" fontWeight={500}>
                          {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
                        </Typography>
                        <Alert variant="gray" text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Details.FeesInfoAlert)} />

                        <Grid container spacing={Spacing.XL}>
                          <Grid size={3}>
                            <BlFormInput
                              control={control}
                              name="entryFee"
                              label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFeeShort)}
                              errors={errors}
                              inputEndAdornment={
                                <Typography variant="body2" color="secondary">
                                  %
                                </Typography>
                              }
                              unmask="typed"
                              mask={[
                                {
                                  mask: Number,
                                  thousandsSeparator: " ",
                                  scale: 2,
                                },
                              ]}
                            />
                          </Grid>
                          <Grid size={3}>
                            <BlFormInput
                              control={control}
                              name="oneTimeFee"
                              label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFeeShort)}
                              errors={errors}
                              inputEndAdornment={
                                <Typography variant="body2" color="secondary">
                                  {t(Resources.Common.CZKSuffix)}
                                </Typography>
                              }
                              unmask="typed"
                              mask={[
                                {
                                  mask: Number,
                                  thousandsSeparator: " ",
                                  scale: 2,
                                },
                              ]}
                            />
                          </Grid>
                          <Grid size={3}>
                            <BlFormInput
                              control={control}
                              name="managementFee"
                              label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFeeShort)}
                              errors={errors}
                              inputEndAdornment={
                                <Typography variant="body2" color="secondary">
                                  %
                                </Typography>
                              }
                              unmask="typed"
                              mask={[
                                {
                                  mask: Number,
                                  thousandsSeparator: " ",
                                  scale: 2,
                                },
                              ]}
                            />
                          </Grid>
                          <Grid size={3}>
                            <BlFormInput
                              control={control}
                              name="exitFee"
                              label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFeeShort)}
                              errors={errors}
                              inputEndAdornment={
                                <Typography variant="body2" color="secondary">
                                  %
                                </Typography>
                              }
                              unmask="typed"
                              mask={[
                                {
                                  mask: Number,
                                  thousandsSeparator: " ",
                                  scale: 2,
                                },
                              ]}
                            />
                          </Grid>
                        </Grid>
                      </Stack>

                      <Divider sx={{ width: "100%" }} />
                      <Box>
                        <CharacteristicsAccordion<InvestmentsFormType>
                          key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                          defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
                        />
                      </Box>
                      <Divider sx={{ width: "100%" }} />
                      <Grid container spacing={Spacing.XL} width="100%">
                        <Grid size={12}>
                          <BlFormInput
                            control={control}
                            label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                            placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                            name="note"
                            errors={errors}
                            multiline
                            minRows={4}
                            maxRows={6}
                            characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </Box>

              <InvestmentsNsureEvaluationPanel hideEvaluationWhenStacked />
            </Flex>
          </Stack>
        </DrawerContent>

        <Flex justifyContent={onDelete ? "space-between" : "flex-end"} px={Spacing.XXL} py={Spacing.XXL}>
          {onDelete && (
            <Button variant="text" color="error" onClick={onDelete}>
              {t(Resources.Common.Delete)}
            </Button>
          )}
          <HStack spacing={Spacing.XL}>
            <Button onClick={onClose} color="secondary">
              {t(Resources.Common.Close)}
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              {t(Resources.Common.Save)}
            </Button>
          </HStack>
        </Flex>
      </>
    </FormProvider>
  );
};
